import { SCREEN_NAME, screenNameSchema } from '@/constants/analytics';
import { NavigationTrigger } from '@/constants/navigation';
import * as MegaMenu from '@/features/mega-menu/MegaMenu.types';
import { searchTrackingPropsSchema, searchVariantSchema } from '@/features/searchV2/types/tracking';
import { z } from 'zod';
import { bookingFlowPropertiesSchema, bookingFlowSchema, sessionIdSchema } from './booking-flow';

/**
 * Required properties in these schemas are NOT enforced anywhere.
 *
 * The app compiles with strictNullChecks off. Zod v3 decides which keys are required
 * with `undefined extends T[K] ? never : K`, and with strictNullChecks off `undefined`
 * is assignable to everything — so that test is true for every key and z.infer marks
 * every property optional. `trackMpEvent({ name: 'booking_flow', properties: {} })`
 * therefore compiles, for every event here. Nothing validates at runtime either: no
 * event schema in this file is ever parsed, so a missing or extra property reaches Amplitude
 * silently.
 *
 * What these types still buy: the *type* of each property is checked, so a bad
 * screen_name or a string where a number belongs is a compile error.
 *
 * Do not mark a property required expecting it to be enforced — say so in the tracking
 * plan instead. Removing this footgun means either turning on strictNullChecks
 * (~1900 errors across ~330 files) or declaring these types by hand, since the event
 * schemas here exist only to derive types.
 */

/**
 * @deprecated
 */
export type TriggerSource = NavigationTrigger | (typeof SCREEN_NAME)[keyof typeof SCREEN_NAME];

const errorShownSchema = z.object({
  name: z.literal('error_shown'),
  properties: z.object({
    screen_name: screenNameSchema,
    error_type: z.string(),
    error_code: z.optional(z.string().regex(/^http_[0-9]{3}$/)),
  }),
});

const screenShownSchema = z.object({
  name: z.literal('screen_shown'),
  properties: z.object({
    screen_name: screenNameSchema,
    search_variant: searchVariantSchema.optional(),
    initiated_from: screenNameSchema.optional(),
    booking_id: z.number().optional(),
    company_id: z.number().optional(),
    customer_id: z.number().optional(),
    author: z.string().optional(),
    category: z.string().optional(),
    post: z.string().optional(),
  }),
});

const megaMenuClickSchema = z.object({
  name: z.literal('mega_menu_click'),
  properties: z.object({
    screen_name: screenNameSchema,
    mega_menu_tab: MegaMenu.tabIdentifierSchema,
    mega_menu_cta: z.string().nullable().optional(),
  }),
});

const companyClickedSchema = z.object({
  name: z.literal('company_clicked'),
  properties: z
    .object({
      screen_name: screenNameSchema,
      marketplace_id: z.number(),
      salon_id: z.number(),
      position: z.number(),
      number_of_ratings: z.string().optional(),
      ratings_average: z.number().optional(),
    })
    .merge(
      searchTrackingPropsSchema.pick({
        campaigns_shown: true,
        search_query: true,
        search_location_filter: true,
        search_location_city: true,
        nbr_search_results: true,
        search_variant: true,
      }),
    ),
});

export const searchPerformedSchema = z.object({
  name: z.literal('search_performed'),
  properties: searchTrackingPropsSchema,
});

export const logoutFlowSchema = z.object({
  name: z.literal('logout_flow'),
  properties: z.object({
    logout_step: z.enum(['initiated', 'successful', 'cancelled']),
  }),
});

export const loginSignupFlowInitiatedFromSchema = z.enum([
  'navigation_bar',
  'navigation_bar_bookings',
  'navigation_bar_favorites',
  'recently_viewed',
  'place_page_favorited',
  'checkout_booking_summary',
  'checkout_booking_summary_force_login',
  'checkout_prior_booking_summary',
  'checkout_prior_bundle_summary',
  'support_form',
  'wellness_card_purchase',
  'footer',
  'footer_bookings',
  'footer_favorites',
  'save_giftcard_from_link',
  'direct_links',
  'profile_page_reviews',
  'finish_account',
]);

export type LoginSignupFlowInitiatedFrom = z.infer<typeof loginSignupFlowInitiatedFromSchema>;

export const loginSignupFlowSchema = z.object({
  name: z.literal('login_sign-up_flow'),
  properties: z.object({
    ['login_sign-up_session_id']: z.string().uuid(),
    initiated_from: loginSignupFlowInitiatedFromSchema,
    step: z.enum([
      'initiated',
      'cancelled',
      'continue_as_guest',
      'create_account',
      'verification',
      'terms_and_conditions',
      'continue_discovery',
      'booking_cancellation',
      'back_to_email_submission',
      'back_to_create_account',
      'resend_code',
      'correct_code',
      'code_submitted',
      'details_added',
      'add_details_later',
      'successful',
      'failed',
      'wrong_code',
      'force_finish_sign_up',
      'prompt',
    ]),
    flow: z.enum(['login', 'sign-up']),
  }),
});

/** An enum in the tracking plan too; values are shared with iOS and Android. */
export const favouriteInitiatedFromSchema = z.enum(['company_details', 'checkout_booking_confirmation', 'favourites']);

const editProfileFlowSchema = z.object({
  name: z.literal('edit_profile_flow'),
  properties: z.object({
    booking_session_id: sessionIdSchema.optional(),
    payment_activation_session_id: sessionIdSchema.optional(),
    buy_bundle_session_id: sessionIdSchema.optional(),
    step: z.enum(['initiated', 'success', 'cancel', 'verification', 'error']),
    initiated_from: screenNameSchema,
    error_type: z.enum(['validation_error', 'backend_error']).optional(),
  }),
});

const deleteAccountFlowSchema = z.object({
  name: z.literal('delete_account_flow'),
  properties: z.object({
    step: z.enum(['initiated', 'success', 'cancel', 'verification', 'error']),
  }),
});

export const addPaymentCardFlowSchema = z.object({
  name: z.literal('add_payment_card_flow'),
  properties: z.object({
    step: z.enum(['initiated', 'verification', 'success', 'cancel', 'error']),
    booking_session_id: sessionIdSchema.optional(),
    payment_activation_session_id: sessionIdSchema.optional(),
    buy_bundle_session_id: sessionIdSchema.optional(),
  }),
});

export const buyBundleFlowSchema = z.object({
  name: z.literal('buy_bundle_flow'),
  properties: bookingFlowPropertiesSchema
    .omit({
      booking_session_id: true,
      booking_id: true,
      booking_type: true,
      days_until_booking: true,
      timeslots_available: true,
      service_ids: true,
    })
    .extend({
      buy_bundle_session_id: sessionIdSchema,
      bundle_id: z.number().optional(),
      bundle_template_id: z.number().or(z.string()).optional(),
    }),
});

export const paymentActivationFlowSchema = z.object({
  name: z.literal('payment_activation_flow'),
  properties: bookingFlowPropertiesSchema
    .omit({ booking_session_id: true, days_until_booking: true, timeslots_available: true })
    .extend({
      payment_activation_session_id: sessionIdSchema,
    }),
});

export declare namespace Tracking {
  type EventOptions = {
    sendToCio?: boolean;
    flush?: boolean;
  };

  export type ScreenName = z.infer<typeof screenNameSchema>;

  export type FavouriteInitiatedFrom = z.infer<typeof favouriteInitiatedFromSchema>;

  type ScreenShown = z.infer<typeof screenShownSchema>;

  type MegaMenuClick = z.infer<typeof megaMenuClickSchema>;

  type CompanyClicked = z.infer<typeof companyClickedSchema>;

  type SearchPerformed = z.infer<typeof searchPerformedSchema>;

  type ErrorShown = z.infer<typeof errorShownSchema>;

  type LogoutFlow = z.infer<typeof logoutFlowSchema>;

  type LoginSignupFlow = z.infer<typeof loginSignupFlowSchema>;

  type EditProfileFlow = z.infer<typeof editProfileFlowSchema>;

  type DeleteAccountFlow = z.infer<typeof deleteAccountFlowSchema>;

  type AddPaymentCardFlow = z.infer<typeof addPaymentCardFlowSchema>;

  type BookingFlow = z.infer<typeof bookingFlowSchema>;

  type BuyBundleFlow = z.infer<typeof buyBundleFlowSchema>;

  type PaymentActivationFlow = z.infer<typeof paymentActivationFlowSchema>;

  /** @deprecated Send events from the tracking schema's Catalogue with `track` from `@/helpers/tracking`. */
  type Event =
    | ScreenShown
    | CompanyClicked
    | MegaMenuClick
    | SearchPerformed
    | ErrorShown
    | LoginSignupFlow
    | LogoutFlow
    | BookingFlow
    | BuyBundleFlow
    | PaymentActivationFlow
    | EditProfileFlow
    | DeleteAccountFlow
    | AddPaymentCardFlow;
}
